/**
 * Per-customer preference: zobrazujeme ceny "s DPH" nebo "bez DPH"?
 *
 * **CZ právo (B2C):** spotřebitelé musí vidět **finální cenu** = gross.
 * Pro anonymous návštěvníky a B2C customers držíme "gross" jako default
 * a nedovolíme přepnout (toggle skryt).
 *
 * **B2B (přihlášený CustomerOrg s vyplněným DIČ):** konvence je `net`
 * (bez DPH) na primární cenovce, gross v secondary line. Toggle
 * povolen; volba se persistuje v ``localStorage`` per device, ne per
 * user — multi-device nutno zvolit dvakrát, ale je to "least
 * surprising" pattern (per browser cookies).
 *
 * Detekce B2B: hooks v ``Cart.tsx`` / ``Checkout.tsx`` zjistí, jestli
 * `useAuthMe()` dodal customer s vyplněným DIČ (``user.dic`` / podobně).
 * Anonymous → vždy B2C → vždy gross.
 */

const STORAGE_KEY = "forgmatic.vatDisplay";

export type VatDisplayMode = "gross" | "net";

export function readVatDisplayPreference(): VatDisplayMode | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "gross" || raw === "net") return raw;
    return null;
  } catch {
    return null;
  }
}

export function writeVatDisplayPreference(mode: VatDisplayMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage může být disabled (Safari private mode); silent.
  }
}

/**
 * Default mode pro daný kontext. ``isB2B=false`` (anonymous / neznámý
 * customer / customer bez DIČ) → vždy ``gross`` (CZ zákon o ochraně
 * spotřebitele). ``isB2B=true`` + saved preference → preference;
 * jinak ``net`` (B2B konvence).
 */
export function defaultVatDisplayMode(
  isB2B: boolean,
  segments?: BuyerSegments | null,
): VatDisplayMode {
  // KAN-1432 — komu tenant prodává, řekl v průvodci. Obchod pro koncové
  // zákazníky proto začíná na ceně s DPH i přihlášené firmě (ta si může
  // přepnout), obchod pro firmy na ceně bez DPH i anonymnímu návštěvníkovi.
  if (segments === "b2c") return readVatDisplayPreference() ?? "gross";
  if (segments === "b2b") return readVatDisplayPreference() ?? "net";
  if (!isB2B) return "gross";
  const saved = readVatDisplayPreference();
  return saved ?? "net";
}

/** Komu tenant prodává — odpověď z průvodce (``business-info``). */
export type BuyerSegments = "b2b" | "b2c" | "both";

/**
 * Jak obchod ukazuje cenu TAM, KDE PŘEPÍNAČ NENÍ — v konfigurátoru a
 * v souhrnu operace v košíku (KAN-1432).
 *
 * Konfigurátor ukazoval „bez DPH" i obchodu, který v průvodci řekl, že
 * prodává koncovým zákazníkům. Cena s DPH se ale nedá vymyslet: potřebuje
 * sazbu, a tu má jen tenant, který je plátce a má zapnutou zemi dopravy.
 * Bez ní zůstává „bez DPH" — nikdy odhad (GENERIC_PLATFORM pravidlo 3).
 *
 * KAN-1498 — a NEPLÁTCI nezůstává ani to: ukazoval se mu popisek
 * „/ ks bez DPH", který slibuje daň, co se někde dál přidá. Neplátce
 * žádnou nemá, takže doložka u ceny odpadá úplně (``appliesVat``).
 *
 * Sazba je NÁHLED: skutečnou určí až doručovací adresa, stejně jako
 * v souhrnu košíku.
 */
/** Režim zobrazení ceny + sazba, se kterou se počítá cena s DPH. */
export interface StorefrontVatDisplay {
  mode: VatDisplayMode;
  ratePct: number;
  /**
   * Účtuje tenant DPH vůbec? (KAN-1498)
   *
   * Neplátce nemá cenu „bez DPH" — nemá žádnou DPH, takže doložka
   * u ceny nemá co říkat a vynechá se celá. Plátce ji má vždycky,
   * i když zrovna neznáme sazbu.
   */
  appliesVat: boolean;
}

export function storefrontVatDisplay(info: {
  buyer_segments?: BuyerSegments | null;
  default_vat_rate_pct?: string | number | null;
  vat_payer?: boolean | null;
} | null | undefined): StorefrontVatDisplay {
  const ratePct = Number(info?.default_vat_rate_pct ?? 0);
  const usable = Number.isFinite(ratePct) && ratePct > 0;
  // KAN-1498 — tohle SE NESMÍ zjednodušit na ``!usable``, i když by to na
  // neplátci vypadalo stejně. Nulovou sazbu vyrobí i PLÁTCE, který ještě
  // nemá zapnutou zemi dopravy nebo v ní nastavenou sazbu — a tam je
  // „bez DPH" pravda: daň existuje, jen ji ještě neumíme spočítat.
  // Rozlišit ty dva stavy jde výhradně příznakem ``vat_payer``.
  // ``undefined`` (business-info se ještě nenačetlo) čteme jako plátce,
  // tedy jako dosud — doložka se smí nejvýš objevit pozdě, nikdy nesmí
  // zmizet plátci.
  const appliesVat = info?.vat_payer !== false;
  // KAN-1656 — obchod, který prodává i koncovým zákazníkům („both"), jim
  // musí od první obrazovky ukázat cenu s DPH; konfigurátor nemá přepínač
  // a nepozná, kdo se dívá. Bez DPH začíná jen obchod čistě pro firmy.
  const sellsToConsumers =
    info?.buyer_segments === "b2c" || info?.buyer_segments === "both";
  return {
    mode: sellsToConsumers && usable && appliesVat ? "gross" : "net",
    ratePct: usable && appliesVat ? ratePct : 0,
    appliesVat,
  };
}

/** Částka přepočtená do zvoleného režimu. ``net`` vrací vstup beze změny. */
export function applyVatDisplay(amount: number, { mode, ratePct }: StorefrontVatDisplay): number {
  return mode === "gross" ? amount * (1 + ratePct / 100) : amount;
}

/**
 * i18n klíč doložky u ceny — tři stavy, ne dva (KAN-1498).
 *
 * Volající si dosadí vlastní namespace + prefix; vrací se jen poslední
 * segment, aby stejné rozhodnutí sloužilo popisku za cenou za kus
 * (``price.unitPriceSuffix*``) i popisku pod součtem sekce košíku
 * (``operationSection.subtotalCaption*``).
 *
 * * neplátce → ``…NoVat`` (žádná doložka o dani, jen jednotka)
 * * plátce, obchod pro koncové zákazníky se známou sazbou → ``…Gross``
 * * plátce jinak → základní klíč („bez DPH")
 */
export function vatSuffixVariant({ mode, appliesVat }: StorefrontVatDisplay): "NoVat" | "Gross" | "" {
  if (!appliesVat) return "NoVat";
  return mode === "gross" ? "Gross" : "";
}
